/**
 * VERBATIM content — mirrors /app/memory/assets/health_quiz_export.md.
 * DO NOT paraphrase. The AI prompts on the backend also reference the
 * same source. When the source is updated, update BOTH files in sync.
 */

/* ------------------------------------------------------------------
 * Phase 1 — 7 baseline questions (Health Check · 1 of 2)
 * ------------------------------------------------------------------*/
export const PHASE1_QUESTIONS = [
    {
        id: "appetite",
        label: "How is your cat's appetite lately?",
        options: [
            "Excellent — eats with enthusiasm",
            "Normal",
            "Picky / reduced",
            "Poor — barely eating",
        ],
    },
    {
        id: "water",
        label: "Daily water intake?",
        options: [
            "Drinks freely several times/day",
            "Drinks a few times daily",
            "Rarely sees them drink",
            "Drinking a LOT more than usual",
        ],
    },
    {
        id: "litter",
        label: "Litter box habits?",
        options: [
            "Normal — regular & well-formed",
            "Occasional soft stool",
            "Constipation / straining",
            "Diarrhea or blood seen",
            "Urinating outside box",
        ],
    },
    {
        id: "energy",
        label: "Energy & play level?",
        options: [
            "Playful, zoomies",
            "Active when invited",
            "Mostly sleeps",
            "Lethargic / withdrawn",
        ],
    },
    {
        id: "weight_trend",
        label: "Weight trend over last 3 months?",
        options: [
            "Stable",
            "Slight gain",
            "Noticeable gain",
            "Slight loss",
            "Noticeable loss",
        ],
    },
    {
        id: "grooming",
        label: "Grooming habits?",
        options: [
            "Normal self-grooming",
            "Over-grooming a spot",
            "Stopped grooming — coat looks unkempt",
            "Excessive shedding",
        ],
    },
    {
        id: "vocal",
        label: "Vocalization changes?",
        options: [
            "Same as usual",
            "Quieter than usual",
            "Much more vocal",
            "Crying / yowling at night",
        ],
    },
];

export const PHASE1_HEADER = "Health Check · 1 of 2";
export const PHASE1_INTRO =
    "Quick baseline — 7 questions about your cat's daily life.";
export const PHASE1_SUBMIT_LABEL = "Continue to Phase 2";

export const PHASE2_HEADER = "Health Check · 2 of 2";
export const PHASE2_INTRO =
    "Personalized follow-ups based on your earlier answers.";
export const PHASE2_SUBMIT_LABEL = "Generate AI report";

/* ------------------------------------------------------------------
 * Loading screens
 * ------------------------------------------------------------------*/
export const PHASE2_LOADING = {
    title: "Analyzing answers…",
    subtitle:
        "Claude Sonnet 4.5 is generating personalized follow-up questions based on your cat's profile.",
    steps: [
        "Scanning your responses…",
        "Cross-checking veterinary data…",
        "Personalizing follow-up questions…",
    ],
};

export const REPORT_LOADING = {
    badge: "MEOWMD AI",
    title: "Generating your report…",
    subtitle:
        "Building an 8-category breakdown, diet plan, and recommendations. This takes ~20–30 seconds.",
    steps: [
        "Scoring 8 health categories…",
        "Building a personalized diet plan…",
        "Writing recommendations…",
        "Adding veterinary references…",
    ],
};

/* ------------------------------------------------------------------
 * Photo check
 * ------------------------------------------------------------------*/
export const PHOTO_CHECK_COPY = {
    eyebrow: "HEALTH PHOTO",
    title: "Snap a clear photo of your cat",
    instructions:
        "Whole body in good light. Claude Vision will flag visible signs — not a diagnosis, but a friendly second look.",
    analyzeLabel: "Analyze with AI",
    changeLabel: "Change photo",
    skipLabel: "Skip photo check",
    analyzing: "Claude is checking the photo…",
    analyzingHint: "Usually 5–10 seconds.",
    backendError:
        "Couldn't analyze that photo. Try a clearer, well-lit shot of the whole cat.",
    fallbackError: "Could not analyze the photo. Try a clearer shot.",
    escalation:
        "This warrants a vet visit. AI is not a substitute for in-person examination.",
    footerDisclaimer:
        "This is a visual screening, not a diagnosis. Always consult a licensed veterinarian for medical concerns.",
    tooLarge: "Photo too large (max ~6MB)",
};

/* ------------------------------------------------------------------
 * Report screen chrome + escalation copy
 * ------------------------------------------------------------------*/
export const REPORT_COPY = {
    scoreBadge: "MEOWMD AI HEALTH REPORT",
    scoreLabel: "HEALTH REPORT SCORE",
    sectionHeadings: {
        emergency: "Emergency alerts",
        critical: "Critical findings",
        categories: "8-category breakdown",
        diet: "Personalized diet plan",
        recommendations: "Recommendations",
        references: "Veterinary references",
        sources: "Sources & further reading",
    },
    emergencyFooter: "See a veterinarian as soon as possible.",
    ctaContinue: "Continue with MeowMD",
    ctaContinueUrl: "https://meowmd.in",
    disclaimerShort:
        "MeowMD provides AI-assisted information and guidance for educational purposes only. It is not veterinary advice, diagnosis, or treatment, and is not a substitute for examination by a licensed veterinarian.",
    disclaimerReadFull: "Read the full Medical Disclaimer",
    categoryStatusLabel: {
        good: "Good",
        monitor: "Monitor",
        concern: "Concern",
    },
};

export const SOURCE_LINKS = [
    {
        label: "Cornell Feline Health Center",
        url: "https://www.vet.cornell.edu/departments-centers-and-institutes/cornell-feline-health-center",
    },
    {
        label: "International Cat Care (iCatCare)",
        url: "https://icatcare.org",
    },
    {
        label: "American Animal Hospital Association (AAHA)",
        url: "https://www.aaha.org",
    },
    {
        label: "WSAVA Global Nutrition Guidelines",
        url: "https://wsava.org/global-guidelines/global-nutrition-guidelines/",
    },
];

/* ------------------------------------------------------------------
 * Verbatim disclaimers
 * ------------------------------------------------------------------*/
export const QUIZ_BANNER_DISCLAIMER =
    "AI-assisted screening — not a diagnosis. For emergencies contact a licensed vet.";

export const FULL_DISCLAIMER_PARAS = [
    "MeowMD provides AI-assisted information and guidance for educational purposes only. It is not veterinary advice, diagnosis, or treatment, and is not a substitute for examination by a licensed veterinarian.",
    "The Health Check tool generates a personalized set of questions and a written report by combining your inputs with a general veterinary knowledge base. The output is a screening aid; it is not a medical opinion about your specific cat and cannot replace hands-on examination, laboratory tests, or imaging.",
    "The Photo Check tool uses computer vision to flag visible signs only. It cannot see or diagnose internal conditions. If your cat is showing signs of distress — laboured breathing, refusal to eat or drink for more than 24 hours, blood in urine or stool, severe lethargy, seizures, obvious pain, sudden collapse — please contact a licensed veterinarian or emergency clinic immediately, without waiting for any AI output.",
    "By using MeowMD Web Tools you acknowledge that the assessment is informational, that you retain full responsibility for decisions about your cat's care, and that MeowMD is not liable for outcomes arising from those decisions. Always defer to a licensed veterinarian for diagnosis and treatment.",
];

export const PDF_FOOTER = [
    "Generated by MeowMD AI — The World's First All-in-One Platform for Cats.",
    "AI-assisted screening, not a diagnosis. Not a substitute for veterinary care.",
];
